import { Injectable, signal } from '@angular/core';

export type VhToastType = 'success' | 'warning' | 'error' | 'info';

export interface VhToast {
  id: number;
  type: VhToastType;
  message: string;
  /** Milliseconds before auto-dismiss. 0 = sticky. */
  duration: number;
}

/**
 * Imperative toast API. Used by feature code to surface transient
 * notifications. `VhToastContainerComponent` must be mounted once per app
 * shell to render the stack.
 */
@Injectable({ providedIn: 'root' })
export class VhToastService {
  private readonly _toasts = signal<VhToast[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private nextId = 1;
  private readonly defaultDuration = 5000;

  success(message: string, duration = this.defaultDuration): void {
    this.push('success', message, duration);
  }

  warning(message: string, duration = this.defaultDuration): void {
    this.push('warning', message, duration);
  }

  error(message: string, duration = this.defaultDuration): void {
    this.push('error', message, duration);
  }

  info(message: string, duration = this.defaultDuration): void {
    this.push('info', message, duration);
  }

  dismiss(id: number): void {
    this._toasts.update((list) => list.filter((t) => t.id !== id));
  }

  private push(type: VhToastType, message: string, duration: number): void {
    const id = this.nextId++;
    this._toasts.update((list) => [...list, { id, type, message, duration }]);
    if (duration > 0) {
      setTimeout(() => this.dismiss(id), duration);
    }
  }
}
